import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Save, ArrowLeft, Plus, Trash2, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";


export default function MonthlyReportSetup() {
  const { user } = useAuth();
  const isOwner = user?.role === "Owner";

  const [allAccounts, setAllAccounts] = useState(null);   // Zoho catalogue
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState({
    revenue_growth_pct: 0,
    revenue_account_id: "",
    accounts: [],
  });

  const loadAll = async () => {
    setError("");
    try {
      const [settingsRes, accRes] = await Promise.all([
        api.get("/monthly-report/settings"),
        api.get("/monthly-report/pnl-accounts"),
      ]);
      setSettings({
        revenue_growth_pct: settingsRes.data.revenue_growth_pct || 0,
        revenue_account_id: settingsRes.data.revenue_account_id || "",
        accounts: settingsRes.data.accounts || [],
      });
      setAllAccounts(accRes.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { if (isOwner) loadAll(); }, [isOwner]);   // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  };

  // Group Zoho accounts by section for nicer picker UX
  const grouped = useMemo(() => {
    const out = {};
    (allAccounts?.accounts || []).forEach((a) => {
      const key = a.section || "Other";
      (out[key] = out[key] || []).push(a);
    });
    Object.keys(out).forEach((k) => out[k].sort((a, b) => a.account_name.localeCompare(b.account_name)));
    return out;
  }, [allAccounts]);

  const selectedIds = useMemo(
    () => new Set(settings.accounts.map((a) => a.account_id)),
    [settings.accounts],
  );

  const toggleAccount = (a) => {
    if (selectedIds.has(a.account_id)) {
      setSettings((s) => ({ ...s, accounts: s.accounts.filter((x) => x.account_id !== a.account_id) }));
    } else {
      setSettings((s) => ({
        ...s,
        accounts: [...s.accounts, {
          account_id: a.account_id,
          account_name: a.account_name,
          target_pct: 0,
        }],
      }));
    }
  };

  const setTargetPct = (account_id, val) => {
    const n = val === "" ? 0 : parseFloat(val);
    setSettings((s) => ({
      ...s,
      accounts: s.accounts.map((a) => a.account_id === account_id ? { ...a, target_pct: isNaN(n) ? 0 : n } : a),
    }));
  };

  const removeAccount = (account_id) => {
    setSettings((s) => ({ ...s, accounts: s.accounts.filter((a) => a.account_id !== account_id) }));
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/monthly-report/settings", settings);
      toast.success("Monthly Report settings saved");
    } catch (e) {
      setError(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (!isOwner) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
        <h2 className="font-display text-2xl">Owner-only page</h2>
        <p className="text-muted-foreground mt-2">Only the Owner can configure the Monthly Report.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mr-setup-page">
      <PageHeader
        eyebrow="Bookkeeping"
        title="Monthly Report — Setup"
        subtitle="Pick which Zoho P&L accounts appear on the report and set a target % for each. Applied to every month going forward."
        actions={
          <div className="flex gap-2">
            <Link to="/monthly-report" className="btn-secondary inline-flex items-center gap-2" data-testid="mr-setup-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <button className="btn-secondary inline-flex items-center gap-2" onClick={refresh} disabled={refreshing} data-testid="mr-setup-refresh">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh from Zoho
            </button>
            <button className="btn-primary inline-flex items-center gap-2" onClick={save} disabled={saving} data-testid="mr-setup-save">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        }
      />

      {error && (
        <div className="border border-destructive/40 bg-destructive/5 text-destructive rounded p-3 text-sm flex items-center gap-2" data-testid="mr-setup-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {allAccounts && !allAccounts.connected && (
        <div className="border border-orange-300 bg-orange-50 text-orange-900 rounded p-3 text-sm">
          Zoho Books isn't connected — {allAccounts.error || "connect it under Admin → Integrations first."}
        </div>
      )}

      {/* --- Revenue target block --- */}
      <section className="card p-5 space-y-4" data-testid="mr-setup-revenue-block">
        <h3 className="font-display text-lg">Revenue target</h3>
        <p className="text-sm text-muted-foreground">
          The Revenue account below is used as <b>Roasted Bean Sales</b> — every line-item target %
          on the report is calculated as a percentage of that number. Growth target is applied to
          the prior-year same month to compute the R target.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-sm space-y-1">
            <span className="label-tiny">Revenue (Roasted Bean Sales) account</span>
            <select
              value={settings.revenue_account_id}
              onChange={(e) => setSettings((s) => ({ ...s, revenue_account_id: e.target.value }))}
              className="w-full border rounded px-3 py-2 bg-background"
              data-testid="mr-setup-revenue-account"
            >
              <option value="">— pick a revenue account —</option>
              {Object.entries(grouped).map(([sec, accs]) => (
                <optgroup key={sec} label={sec}>
                  {accs.map((a) => (
                    <option key={a.account_id} value={a.account_id}>
                      {a.account_name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span className="label-tiny">Growth target % (vs prior-year same month)</span>
            <input
              type="number" step="0.1" min="-100" max="500"
              value={settings.revenue_growth_pct}
              onChange={(e) => setSettings((s) => ({ ...s, revenue_growth_pct: parseFloat(e.target.value) || 0 }))}
              className="w-full border rounded px-3 py-2 bg-background tabular-nums"
              data-testid="mr-setup-growth-pct"
            />
          </label>
        </div>
      </section>

      {/* --- Selected accounts --- */}
      <section className="card p-5" data-testid="mr-setup-selected-block">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg">Selected P&amp;L lines ({settings.accounts.length})</h3>
        </div>
        {settings.accounts.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No accounts picked yet. Toggle rows in the catalogue below to add them.
          </p>
        )}
        {settings.accounts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground uppercase tracking-wider border-b">
                <tr>
                  <th className="text-left py-2">Account</th>
                  <th className="text-right py-2 w-32">Target %</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {settings.accounts.map((a) => (
                  <tr key={a.account_id} className="border-b" data-testid={`mr-setup-selected-${a.account_id}`}>
                    <td className="py-2">{a.account_name}</td>
                    <td className="text-right py-2">
                      <input
                        type="number" step="0.1"
                        value={a.target_pct}
                        onChange={(e) => setTargetPct(a.account_id, e.target.value)}
                        className="w-24 border rounded px-2 py-1 text-right tabular-nums bg-background"
                        data-testid={`mr-setup-target-${a.account_id}`}
                      /> <span className="text-xs text-muted-foreground">%</span>
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => removeAccount(a.account_id)} className="text-muted-foreground hover:text-destructive" data-testid={`mr-setup-remove-${a.account_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --- Full catalogue --- */}
      <section className="card p-5" data-testid="mr-setup-catalogue-block">
        <h3 className="font-display text-lg mb-3">P&amp;L account catalogue</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Every account posted-to in the last 12 months. Click to add to the report.
        </p>
        {!allAccounts && <p className="text-sm text-muted-foreground">Loading from Zoho…</p>}
        {allAccounts?.connected && Object.keys(grouped).length === 0 && (
          <p className="text-sm text-muted-foreground">No accounts found — is your Zoho org empty for the last year?</p>
        )}
        <div className="space-y-4">
          {Object.entries(grouped).map(([sec, accs]) => (
            <div key={sec}>
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{sec}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {accs.map((a) => {
                  const on = selectedIds.has(a.account_id);
                  return (
                    <button
                      key={a.account_id}
                      onClick={() => toggleAccount(a)}
                      data-testid={`mr-setup-toggle-${a.account_id}`}
                      className={`text-left border rounded px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                        on ? "border-primary bg-primary/10" : "hover:bg-secondary/50"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        on ? "bg-primary border-primary text-white" : "border-muted-foreground"
                      }`}>
                        {on ? "✓" : ""}
                      </span>
                      <span className="flex-1 truncate">{a.account_name}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        R{Math.round(a.total).toLocaleString("en-ZA")}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
